import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  IconButton,
  Chip,
  Stack,
  Paper,
} from "@mui/material";
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material";

export default function ProductChoiceSelector({
  open,
  onClose,
  product,
  selectedCombination,
  units,
  onChoicesSelected,
  onAddToCart,
  resetAndPreview,
  isProductAddonsEnabled,
  checkProductAddons,
  setShowAddonSelector,
  setParentSelectedChoices,
}) {
  // selectedChoices: { [category]: string | string[] }
  const [selectedChoices, setSelectedChoices] = useState({});
  // choiceOptions: { [category]: { options: string[], is_single: boolean } }
  const [choiceOptions, setChoiceOptions] = useState({});

  // Parse choice options when dialog opens
  useEffect(() => {
    if (open && product) {
      parseChoiceOptions();
    }
  }, [open, product]);

  const parseChoiceOptions = () => {
    try {
      if (product.Choice_Options) {
        let parsed;
        if (typeof product.Choice_Options === "string") {
          parsed = JSON.parse(product.Choice_Options);
        } else {
          parsed = product.Choice_Options;
        }

        // Handle new format: [{name, options, is_single}]
        const optionsObject = {};
        if (Array.isArray(parsed)) {
          parsed.forEach((choice) => {
            if (
              choice.name &&
              Array.isArray(choice.options) &&
              choice.options.length > 0
            ) {
              optionsObject[choice.name] = {
                options: choice.options,
                is_single:
                  typeof choice.is_single === "boolean"
                    ? choice.is_single
                    : false, // backward compatibility
              };
            }
          });
        }

        setChoiceOptions(optionsObject);

        // Initialize selected choices
        const initialSelections = {};
        Object.entries(optionsObject).forEach(
          ([category, { options, is_single }]) => {
            if (options && options.length > 0) {
              initialSelections[category] = is_single ? options[0] : [];
            }
          }
        );
        setSelectedChoices(initialSelections);
      } else {
        setChoiceOptions({});
        setSelectedChoices({});
      }
    } catch (error) {
      console.error("Error parsing choice options:", error);
      setChoiceOptions({});
      setSelectedChoices({});
    }
  };

  // For radio (single): value is string
  // For checkbox (multi): value is array of strings
  const handleChoiceChange = (category, value, is_single) => {
    setSelectedChoices((prev) => {
      if (is_single) {
        return {
          ...prev,
          [category]: value,
        };
      } else {
        // value is option string
        const prevArr = Array.isArray(prev[category]) ? prev[category] : [];
        let newArr;
        if (prevArr.includes(value)) {
          newArr = prevArr.filter((v) => v !== value);
        } else {
          newArr = [...prevArr, value];
        }
        return {
          ...prev,
          [category]: newArr,
        };
      }
    });
  };

  const handleContinue = async () => {
    // First, check if addons are enabled and available
    if (
      isProductAddonsEnabled &&
      checkProductAddons &&
      setShowAddonSelector &&
      setParentSelectedChoices
    ) {
      try {
        const availableAddons = await checkProductAddons();
        if (availableAddons && availableAddons.length > 0) {
          // If addons are available, set selected choices and show addon selector
          setParentSelectedChoices(selectedChoices);
          setShowAddonSelector(true);
          onClose(); // Close choice selector dialog
          return;
        }
      } catch (error) {
        console.log(
          "Error checking addons, proceeding with normal cart addition"
        );
      }
    }

    // If no addons available or addons feature disabled, add directly to cart
    if (onAddToCart) {
      // When choice options are selected, always create a new cart item
      const hasChoiceOptions =
        selectedChoices && Object.keys(selectedChoices).length > 0;
      await onAddToCart(selectedChoices, hasChoiceOptions);
      // Reset and refresh after successful cart addition
      if (resetAndPreview) {
        resetAndPreview();
      }
    }

    // Clear local selections and close dialog
    setSelectedChoices({});
    onClose();
  };

  const handleClose = () => {
    setSelectedChoices({});
    onClose();
  };

  const hasChoices = Object.keys(choiceOptions).length > 0;

  if (!product) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: "70vh",
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle sx={{ pb: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h5" fontWeight="600" color="primary">
              Choose Your Options
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Select your preferred options for this product
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="medium">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 1 }}>
        {!hasChoices ? (
          <Box textAlign="center" py={4}>
            <Typography variant="h6" color="text.secondary">
              No options available for this product
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {/* Left Column - Product Summary */}
            <Grid item xs={12} md={4}>
              <Box sx={{ position: "sticky", top: 0 }}>
                <Paper elevation={2} sx={{ p: 2.5, borderRadius: 2, mb: 2 }}>
                  <Typography variant="h6" fontWeight="600" gutterBottom>
                    {product.Product_Full_Name}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    {selectedCombination?.Combination_Text} × {units}
                  </Typography>
                  <Chip
                    label={`${Object.keys(choiceOptions).length} choice${
                      Object.keys(choiceOptions).length > 1 ? "s" : ""
                    } available`}
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                </Paper>

                {/* Selected Summary - Compact */}
                {Object.keys(selectedChoices).length > 0 && (
                  <Paper
                    elevation={1}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: (theme) =>
                        theme.palette.mode === "dark"
                          ? theme.palette.grey[800]
                          : theme.palette.primary.light + "15",
                      border: 1,
                      borderColor: "primary.light",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      fontWeight="600"
                      gutterBottom
                      color="primary"
                    >
                      Your Selection
                    </Typography>
                    <Stack spacing={1}>
                      {Object.entries(selectedChoices).map(
                        ([category, value]) => (
                          <Box
                            key={category}
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ fontSize: "0.875rem" }}
                            >
                              {category}:
                            </Typography>
                            {/* Show chips for multi-select, single chip for single-select */}
                            {choiceOptions[category]?.is_single ? (
                              <Chip
                                label={value}
                                size="small"
                                color="primary"
                                variant="filled"
                                sx={{
                                  height: 24,
                                  fontSize: "0.75rem",
                                  fontWeight: 500,
                                }}
                              />
                            ) : (
                              <Stack direction="row" spacing={0.5}>
                                {(Array.isArray(value) ? value : []).map(
                                  (v, idx) => (
                                    <Chip
                                      key={idx}
                                      label={v}
                                      size="small"
                                      color="primary"
                                      variant="filled"
                                      sx={{
                                        height: 24,
                                        fontSize: "0.75rem",
                                        fontWeight: 500,
                                      }}
                                    />
                                  )
                                )}
                                {(!value || value.length === 0) && (
                                  <Chip
                                    label="None"
                                    size="small"
                                    color="default"
                                    variant="outlined"
                                    sx={{ height: 24, fontSize: "0.75rem" }}
                                  />
                                )}
                              </Stack>
                            )}
                          </Box>
                        )
                      )}
                    </Stack>
                  </Paper>
                )}
              </Box>
            </Grid>

            {/* Right Column - Choice Options */}
            <Grid item xs={12} md={8}>
              <Stack spacing={2.5}>
                {Object.entries(choiceOptions).map(
                  ([category, { options, is_single }]) => (
                    <Card
                      key={category}
                      sx={{ borderRadius: 2, overflow: "hidden" }}
                    >
                      <Box
                        sx={{
                          bgcolor: "primary.main",
                          color: "primary.contrastText",
                          p: 1.5,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography variant="subtitle1" fontWeight="600">
                          {category}
                        </Typography>
                        <Chip
                          label={`${options.length} options`}
                          size="small"
                          sx={{
                            bgcolor: "rgba(255,255,255,0.2)",
                            color: "inherit",
                            height: 24,
                          }}
                        />
                        <Chip
                          label={
                            is_single ? "Single choice" : "Multiple choices"
                          }
                          size="small"
                          sx={{
                            bgcolor: is_single ? "success.light" : "info.light",
                            color: "inherit",
                            height: 24,
                            ml: 1,
                          }}
                        />
                      </Box>
                      <CardContent sx={{ p: 2 }}>
                        {is_single ? (
                          <RadioGroup
                            value={selectedChoices[category] || ""}
                            onChange={(e) =>
                              handleChoiceChange(category, e.target.value, true)
                            }
                          >
                            <Grid container spacing={1.5}>
                              {options.map((option, index) => (
                                <Grid item xs={6} sm={4} md={3} key={index}>
                                  <FormControlLabel
                                    value={option}
                                    control={
                                      <Radio color="primary" size="small" />
                                    }
                                    label={
                                      <Typography
                                        variant="body2"
                                        sx={{ fontSize: "0.875rem" }}
                                      >
                                        {option}
                                      </Typography>
                                    }
                                    sx={{
                                      border: 1,
                                      borderColor:
                                        selectedChoices[category] === option
                                          ? "primary.main"
                                          : "divider",
                                      borderRadius: 1.5,
                                      m: 0,
                                      p: 1,
                                      width: "100%",
                                      transition: "all 0.2s ease",
                                      backgroundColor:
                                        selectedChoices[category] === option
                                          ? (theme) =>
                                              theme.palette.mode === "dark"
                                                ? theme.palette.primary.dark +
                                                  "20"
                                                : theme.palette.primary.light +
                                                  "30"
                                          : "transparent",
                                      "&:hover": {
                                        borderColor: "primary.main",
                                        backgroundColor: (theme) =>
                                          theme.palette.mode === "dark"
                                            ? theme.palette.primary.dark + "10"
                                            : theme.palette.primary.light +
                                              "15",
                                      },
                                      "& .MuiFormControlLabel-label": {
                                        fontSize: "0.875rem",
                                        fontWeight:
                                          selectedChoices[category] === option
                                            ? 500
                                            : 400,
                                      },
                                    }}
                                  />
                                </Grid>
                              ))}
                            </Grid>
                          </RadioGroup>
                        ) : (
                          <Grid container spacing={1.5}>
                            {options.map((option, index) => (
                              <Grid item xs={6} sm={4} md={3} key={index}>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={
                                        Array.isArray(
                                          selectedChoices[category]
                                        ) &&
                                        selectedChoices[category].includes(
                                          option
                                        )
                                      }
                                      onChange={() =>
                                        handleChoiceChange(
                                          category,
                                          option,
                                          false
                                        )
                                      }
                                      color="primary"
                                      size="small"
                                    />
                                  }
                                  label={
                                    <Typography
                                      variant="body2"
                                      sx={{ fontSize: "0.875rem" }}
                                    >
                                      {option}
                                    </Typography>
                                  }
                                  sx={{
                                    border: 1,
                                    borderColor:
                                      Array.isArray(
                                        selectedChoices[category]
                                      ) &&
                                      selectedChoices[category].includes(option)
                                        ? "primary.main"
                                        : "divider",
                                    borderRadius: 1.5,
                                    m: 0,
                                    p: 1,
                                    width: "100%",
                                    transition: "all 0.2s ease",
                                    backgroundColor:
                                      Array.isArray(
                                        selectedChoices[category]
                                      ) &&
                                      selectedChoices[category].includes(option)
                                        ? (theme) =>
                                            theme.palette.mode === "dark"
                                              ? theme.palette.primary.dark +
                                                "20"
                                              : theme.palette.primary.light +
                                                "30"
                                        : "transparent",
                                    "&:hover": {
                                      borderColor: "primary.main",
                                      backgroundColor: (theme) =>
                                        theme.palette.mode === "dark"
                                          ? theme.palette.primary.dark + "10"
                                          : theme.palette.primary.light + "15",
                                    },
                                    "& .MuiFormControlLabel-label": {
                                      fontSize: "0.875rem",
                                      fontWeight:
                                        Array.isArray(
                                          selectedChoices[category]
                                        ) &&
                                        selectedChoices[category].includes(
                                          option
                                        )
                                          ? 500
                                          : 400,
                                    },
                                  }}
                                />
                              </Grid>
                            ))}
                          </Grid>
                        )}
                      </CardContent>
                    </Card>
                  )
                )}
              </Stack>
            </Grid>
          </Grid>
        )}
      </DialogContent>

      <DialogActions
        sx={{ p: 2.5, pt: 1.5, borderTop: 1, borderColor: "divider" }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          width="100%"
        >
          <Button
            onClick={handleClose}
            size="large"
            color="inherit"
            sx={{ px: 3 }}
          >
            Cancel
          </Button>
          <Box display="flex" alignItems="center" gap={2}>
            {Object.keys(selectedChoices).length > 0 && (
              <Typography variant="body2" color="text.secondary">
                {Object.keys(selectedChoices).length} of{" "}
                {Object.keys(choiceOptions).length} choices made
              </Typography>
            )}
            <Button
              variant="contained"
              onClick={handleContinue}
              size="large"
              startIcon={<ArrowForwardIcon />}
              disabled={
                !hasChoices || Object.keys(selectedChoices).length === 0
              }
              sx={{
                borderRadius: 1.5,
                px: 4,
                py: 1.2,
                fontSize: "1rem",
                fontWeight: 600,
                minWidth: 180,
              }}
            >
              Continue to Cart
            </Button>
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
